/**
 * TheSportsDB response normalization — provider quirks → canonical sports dataShape.
 *
 * SERVER-SIDE ONLY. Validates the raw TheSportsDB payload (already Zod-parsed)
 * and maps it to the metrics declared in DATA_SHAPE_KEYS['sports.score'].
 *
 * Immutability: returns a fresh object, never mutates the input.
 *
 * TheSportsDB API docs: https://www.thesportsdb.com/api.php
 * Endpoint used: GET https://www.thesportsdb.com/api/v1/json/{apiKey}/eventslast.php?id={teamId}
 *
 * ENDPOINT CHOICE (documented):
 *   We use `eventslast.php?id={teamId}` — the "last 5 events for a team" endpoint.
 *   This gives us the most recent completed match, which is what the score tile
 *   should display: "your team's last result". An alternative (`eventsseason.php`)
 *   gives the full season but requires the season year as a param, making it
 *   harder to stay current. The `livescore.php` endpoint requires the $9/mo live
 *   tier (D-010) and returns the CURRENT game in progress — we use eventslast
 *   to get the latest completed score, which is always available on the base tier.
 *
 * D-010 CONSTRAINTS:
 *   - 2-minute delayed data (TheSportsDB $9/mo tier)
 *   - Attribution required: "Data from TheSportsDB.com" must be shown in tile chrome
 *   - Link-out for live data: if a game is currently in progress, we show the last
 *     completed score and mark status as 'NS' (Not Started) or 'FT' (Full Time)
 *
 * NOTE: DATA_SHAPE_KEYS['sports.score'] does NOT include an `attribution` field.
 * Attribution belongs in the tile chrome (rendered by platform-dev), not in the
 * data shape (which is tile-type-agnostic). This is flagged for review — see the
 * tile-dev final report. The provider attribution is: "Data from TheSportsDB.com".
 *
 * SCORE FORMAT:
 *   `score` field contains the score string for the primary slot, e.g.:
 *     "3-1" (soccer), "102-98" (basketball), "W 3-1" or "L 1-3" (prefixed with result)
 *   We normalize to "W 3-1" format when the result is known, or "3-1" when neutral.
 *   This fits the 8-char primary slot budget ("W 3-1" = 5 chars, "102-98" = 6 chars).
 */

import { z } from 'zod';

// ---------------------------------------------------------------------------
// Raw TheSportsDB event response schema.
// SERVER-SIDE ONLY — the client never sees raw provider payloads (ADR-002 §5).
// ---------------------------------------------------------------------------

/**
 * Zod schema for a single TheSportsDB event (match) entry.
 * Ref: https://www.thesportsdb.com/api.php — eventslast.php response
 *
 * Fields we consume:
 *   idEvent        — unique event ID
 *   strEvent       — event name/description
 *   strHomeTeam    — home team name
 *   strAwayTeam    — away team name
 *   intHomeScore   — home team score (string or null; null if not played)
 *   intAwayScore   — away team score (string or null)
 *   strStatus      — match status: "Match Finished", "Not Started", etc.
 *   dateEvent      — date of the event (YYYY-MM-DD)
 *   strTime        — time of the event (HH:MM:SS or null)
 *   idHomeTeam     — numeric home team ID (as string)
 *   idAwayTeam     — numeric away team ID (as string)
 *   strResult      — sometimes populated with "W" / "L" / "D" (optional)
 *
 * TheSportsDB returns scores as strings ("3", "102") or null if not played.
 * We use `.nullable()` and `.optional()` to handle both absences.
 *
 * Ref: https://www.thesportsdb.com/api/v1/json/3/eventslast.php?id=133602
 */
export const rawSportsDbEventSchema = z.object({
  idEvent: z.string(),
  strEvent: z.string().optional(),
  strHomeTeam: z.string(),
  strAwayTeam: z.string(),
  intHomeScore: z.string().nullable().optional(),
  intAwayScore: z.string().nullable().optional(),
  strStatus: z.string().nullable().optional(),
  dateEvent: z.string().optional(), // YYYY-MM-DD
  strTime: z.string().nullable().optional(), // HH:MM:SS or null
  idHomeTeam: z.string().optional(),
  idAwayTeam: z.string().optional(),
  strResult: z.string().nullable().optional(), // "W", "L", "D" sometimes populated
});

/**
 * Zod schema for the TheSportsDB eventslast.php response.
 * The response wraps events in: { events: [...] | null }
 * Events is null when no recent events exist (new team, no games played yet).
 *
 * Ref: https://www.thesportsdb.com/api.php
 */
export const rawSportsDbResponseSchema = z.object({
  events: z.array(rawSportsDbEventSchema).nullable(),
});

export type RawSportsDbEvent = z.infer<typeof rawSportsDbEventSchema>;
export type RawSportsDbResponse = z.infer<typeof rawSportsDbResponseSchema>;

// ---------------------------------------------------------------------------
// Normalized sports data shape — the canonical output of normalize().
// Keys MUST match DATA_SHAPE_KEYS['sports.score']:
//   score, clock, status, opponent, result, nextFixtureAt
// ---------------------------------------------------------------------------

/**
 * Normalized sports metrics. Exported so glanceView and tests can reference
 * the type without importing from the raw shape.
 *
 * `score` : formatted score string, ready for the primary slot: "3-1", "102-98".
 *           May be empty string when no completed game exists.
 *
 * `clock` : game clock/time context (empty for completed games, "FT" for finished,
 *           or minutes remaining for live games). Empty string when not applicable.
 *
 * `status`: normalized status string from TheSportsDB, e.g.:
 *           "FT" (full time / finished), "NS" (not started),
 *           "1H" / "HT" / "2H" (in-progress half indicators),
 *           "" (unknown/unavailable)
 *
 * `opponent`: name of the opposing team.
 *
 * `result` : "W" | "L" | "D" | "" — win/loss/draw result for completed games.
 *            Empty string when the game has not been played or result is unknown.
 *
 * `nextFixtureAt`: ISO datetime string of the next upcoming fixture, or null
 *                  if no upcoming fixture is available. Note: eventslast.php
 *                  returns the LAST 5 completed games. We use eventsnext.php
 *                  for the next fixture... but in v1 we only fetch eventslast
 *                  (one endpoint call). nextFixtureAt is therefore always null
 *                  in v1 (documented limitation). A v2 upgrade can add a second
 *                  call to eventsnext.php and merge the two results.
 */
export interface SportsData {
  /** Score string for the primary slot: "3-1" or "102-98". Empty if no game. */
  readonly score: string;
  /** Game clock context: "FT", "HT", "1H", "2H", or "" for completed/unavailable. */
  readonly clock: string;
  /** Normalized status: "FT" | "NS" | "live" | "" */
  readonly status: string;
  /** Opposing team name. */
  readonly opponent: string;
  /** Result for completed games: "W" | "L" | "D" | "" */
  readonly result: string;
  /** Next fixture datetime (ISO string) or null (always null in v1 — see above). */
  readonly nextFixtureAt: string | null;
}

/**
 * Context injected alongside the raw payload (server-side).
 * `teamId` is the TheSportsDB team ID we queried — used to determine
 * which team is "ours" (home vs away) when parsing the score.
 */
export interface SportsNormalizeContext {
  /** TheSportsDB team ID (as passed in params.teamId). */
  readonly teamId: string;
}

/**
 * Normalize a TheSportsDB status string to our canonical status set.
 *
 * TheSportsDB uses: "Match Finished" / "Not Started" / "1H" / "HT" / "2H" etc.
 * We normalize to: "FT" | "NS" | "live" | "".
 */
function normalizeStatus(raw: string | null | undefined): string {
  if (!raw) return '';
  const s = raw.toLowerCase();
  if (s === 'match finished' || s === 'ft' || s === 'aet' || s === 'pen') return 'FT';
  if (s === 'not started' || s === 'ns') return 'NS';
  if (s === '1h' || s === 'ht' || s === '2h' || s === 'et' || s === 'live' || s === 'in play') {
    return 'live';
  }
  return '';
}

/**
 * Determine W/L/D result for "our" team given the event and which team ID is ours.
 *
 * Returns "" when the game has not been completed or scores are unavailable.
 */
function deriveResult(
  event: RawSportsDbEvent,
  teamId: string,
): 'W' | 'L' | 'D' | '' {
  const homeScore = event.intHomeScore != null ? parseInt(event.intHomeScore, 10) : null;
  const awayScore = event.intAwayScore != null ? parseInt(event.intAwayScore, 10) : null;

  if (homeScore === null || awayScore === null) return '';

  // Determine if we're the home or away team by comparing IDs.
  // TheSportsDB returns idHomeTeam/idAwayTeam as strings.
  const isHome = event.idHomeTeam === teamId;
  const isAway = event.idAwayTeam === teamId;

  if (!isHome && !isAway) {
    // teamId doesn't match either team — can't determine result
    return '';
  }

  const ourScore = isHome ? homeScore : awayScore;
  const theirScore = isHome ? awayScore : homeScore;

  if (ourScore > theirScore) return 'W';
  if (ourScore < theirScore) return 'L';
  return 'D';
}

/**
 * Map a TheSportsDB eventslast response → canonical SportsData.
 *
 * Pure: returns a fresh object, never mutates `raw`.
 * Total: handles null events array (no recent games), null scores (unplayed).
 *
 * Selects the most-recent event by sorting descending on dateEvent + strTime.
 * We do NOT assume the API's ordering (TheSportsDB documentation says
 * most-recent-first in practice, but this varies; sorting is robust to both).
 *
 * Context.teamId must be TheSportsDB's numeric team id (as a string) — used to
 * determine which team is "ours" (home vs away) when parsing the score.
 */
export function normalize(
  raw: RawSportsDbResponse,
  context: SportsNormalizeContext,
): SportsData {
  const events = raw.events;

  // No events → all fields empty/null.
  if (!events || events.length === 0) {
    return {
      score: '',
      clock: '',
      status: '',
      opponent: '',
      result: '',
      nextFixtureAt: null,
    };
  }

  // Sort descending by dateEvent + strTime so the most-recent event is always first,
  // regardless of the ordering TheSportsDB returns.
  const sorted = events.slice().sort((a, b) => {
    const aKey = `${a.dateEvent ?? ''}T${a.strTime ?? '00:00:00'}`;
    const bKey = `${b.dateEvent ?? ''}T${b.strTime ?? '00:00:00'}`;
    return bKey.localeCompare(aKey);
  });

  const event = sorted[0]!;

  const homeScore = event.intHomeScore != null ? parseInt(event.intHomeScore, 10) : null;
  const awayScore = event.intAwayScore != null ? parseInt(event.intAwayScore, 10) : null;

  // Build the score string: "3-1" or "102-98"
  let score = '';
  if (homeScore !== null && awayScore !== null) {
    score = `${homeScore}-${awayScore}`;
  }

  const status = normalizeStatus(event.strStatus);
  const result = deriveResult(event, context.teamId);

  // Determine opponent name.
  // When neither team ID matches context.teamId we cannot determine which team
  // is ours, so we return '' — never guess the wrong team as the opponent.
  const isHome = event.idHomeTeam === context.teamId;
  const isAway = event.idAwayTeam === context.teamId;
  const opponent = isHome ? event.strAwayTeam : isAway ? event.strHomeTeam : '';

  // clock: for completed games, use status; for live use the status label
  let clock = '';
  if (status === 'FT') clock = 'FT';
  else if (status === 'live') clock = event.strStatus ?? 'live';

  return {
    score,
    clock,
    status,
    opponent,
    result,
    nextFixtureAt: null, // v1: no eventsnext.php call; always null
  };
}
